import { createPortal } from 'react-dom';
import { useEffect, useId, useRef } from 'react';
import CloseButton from '../CloseButton';

// daisyUI's modal-box defaults to a narrow width; honour an explicit size so
// detail-heavy modals (e.g. review) get the room their authors intended.
const SIZE_WIDTHS = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-5xl',
};

const Modal = ({ isOpen, onClose, title, children, actions, size, centered = false, nativeDialog = false, closeDisabled = false }) => {
  const dialogRef = useRef(null);
  const boxRef = useRef(null);
  const isCentered = centered && !nativeDialog;
  useEffect(() => {
    if (!isOpen || !nativeDialog) return undefined;
    const dialog = dialogRef.current;
    dialog.showModal();
    dialog.querySelector('input:not([type="hidden"]), textarea, select')?.focus({ preventScroll: true });
    boxRef.current.scrollTop = 0;
    return () => dialog.close();
  }, [isOpen, nativeDialog]);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isOpen || !isCentered) return;
    const previous = document.activeElement;
    const box = boxRef.current;
    (box?.querySelector('input') || box?.querySelector('button'))?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [isOpen, isCentered]);
  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); if (!closeDisabled) closeRef.current?.(); }
    if (event.key !== 'Tab') return;
    const buttons = [...boxRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')];
    const first = buttons[0], last = buttons.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  if (!isOpen) return null;

  const widthClass = SIZE_WIDTHS[size] || '';
  const Container = nativeDialog ? 'dialog' : 'div';

  return createPortal(
    <Container className={isCentered ? 'fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4' : 'modal modal-open'}
      ref={nativeDialog ? dialogRef : undefined}
      aria-label={nativeDialog && typeof title === 'string' ? title : undefined}
      onCancel={nativeDialog ? event => { event.preventDefault(); if (!closeDisabled) onClose?.(); } : undefined}
      onClick={isCentered ? event => { if (event.target === event.currentTarget && !closeDisabled) onClose?.(); } : undefined}>
      <div ref={boxRef} onKeyDown={isCentered ? onKeyDown : undefined} role={isCentered ? 'dialog' : undefined} aria-modal={isCentered ? true : undefined}
        aria-labelledby={isCentered ? titleId : undefined}
        className={isCentered ? `w-full ${widthClass || 'max-w-lg'} max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-base-100 p-5 shadow-xl sm:p-6`
          : `modal-box max-h-[calc(100vh-120px)] overflow-y-auto mt-16 ${widthClass}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 id={titleId} className="font-bold text-lg">{title}</h3>
          {isCentered ? <CloseButton onClick={closeDisabled ? undefined : onClose} /> : <button
            type={nativeDialog ? 'button' : undefined}
            disabled={closeDisabled}
            aria-label={nativeDialog ? 'Close' : undefined}
            className="btn btn-sm btn-circle btn-ghost"
            onClick={onClose}
          >
            ✕
          </button>}
        </div>
        <div className={isCentered ? '' : 'py-4'}>
          {children}
        </div>
        {actions && (
          <div className="modal-action">
            {actions}
          </div>
        )}
      </div>
    </Container>,
    document.body
  );
};

export default Modal;
